import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { W } from "./wizardTokens";
import FlagBanner from "./FlagBanner";
import { LETTERS, TYPE_LABEL, answerSummary, hasCorrectAnswer, nextLocalId, normalizeQuestion, totalPoints } from "./reviewModel";

const inputSx = { width: "100%", height: 40, px: "12px", border: `1px solid ${W.line}`, borderRadius: "6px", font: "inherit", fontSize: "14px", bgcolor: "#fff", "&:focus": { outline: "none", borderColor: W.primary } };

function Chevron({ open }) {
  return (
    <Box component="svg" width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"
      sx={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .15s", color: W.ink3 }}>
      <path d="m6 9 6 6 6-6" />
    </Box>
  );
}

function WarnIcon() {
  return (
    <Box component="svg" width={11} height={11} viewBox="0 0 24 24" fill="none" stroke={W.danger} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3 2 21h20L12 3z" /><path d="M12 10v5" /><path d="M12 18h.01" />
    </Box>
  );
}

/** Multiple choice (one correct) or multiple select (any number correct): click a letter to mark it. */
function ChoiceEditor({ q, onChange }) {
  const single = q.type !== "MULTIPLE_SELECT";
  const setOption = (idx, patch) => onChange({ options: q.options.map((o, i) => (i === idx ? { ...o, ...patch } : o)) });
  const markCorrect = (idx) => onChange({
    options: q.options.map((o, i) => (single ? { ...o, isCorrect: i === idx } : i === idx ? { ...o, isCorrect: !o.isCorrect } : o)),
  });
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {q.options.map((o, i) => (
        <Box key={o.id} sx={{ display: "grid", gridTemplateColumns: "34px minmax(0,1fr) 76px", gap: "8px", alignItems: "center", p: "4px", borderRadius: "8px", border: `1px solid ${o.isCorrect ? W.success : "transparent"}`, bgcolor: o.isCorrect ? W.successBg : "transparent" }}>
          <Box component="button" type="button" onClick={() => markCorrect(i)} aria-pressed={o.isCorrect}
            aria-label={`Mark choice ${LETTERS[i]} correct`} title={o.isCorrect ? "Correct answer" : "Mark as correct"}
            sx={{ width: 28, height: 28, mx: "auto", borderRadius: single ? "50%" : "6px", border: `1.5px solid ${o.isCorrect ? W.success : W.lineCool}`, bgcolor: o.isCorrect ? W.success : "#fff", color: o.isCorrect ? "#fff" : W.ink2, font: "inherit", fontSize: "12px", fontWeight: 700, cursor: "pointer", display: "grid", placeItems: "center", p: 0 }}>
            {o.isCorrect
              ? <Box component="svg" width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></Box>
              : LETTERS[i]}
          </Box>
          <Box component="input" value={o.text || ""} aria-label={`Choice ${LETTERS[i]}`} onChange={(e) => setOption(i, { text: e.target.value })}
            sx={{ ...inputSx, fontWeight: o.isCorrect ? 600 : 400, color: o.isCorrect ? W.success : W.ink, borderColor: o.isCorrect ? W.success : W.line }} />
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "4px" }}>
            {o.isCorrect && <Box sx={{ fontSize: "10.5px", fontWeight: 800, letterSpacing: ".06em", color: W.success }}>CORRECT</Box>}
            {!o.isCorrect && q.options.length > 2 && (
              <Box component="button" type="button" aria-label={`Remove choice ${LETTERS[i]}`} onClick={() => onChange({ options: q.options.filter((_, k) => k !== i) })}
                sx={{ border: 0, bgcolor: "transparent", color: W.ink4, cursor: "pointer", fontSize: "13px", "&:hover": { color: W.danger } }}>✕</Box>
            )}
          </Box>
        </Box>
      ))}
      {q.options.length < LETTERS.length && (
        <Box component="button" type="button" onClick={() => onChange({ options: [...q.options, { id: nextLocalId(), text: "", isCorrect: false }] })}
          sx={{ alignSelf: "flex-start", ml: "46px", font: "inherit", fontSize: "12.5px", fontWeight: 700, color: W.ai, border: 0, bgcolor: "transparent", cursor: "pointer", p: 0 }}>
          + Add choice
        </Box>
      )}
    </Box>
  );
}

function TrueFalseEditor({ q, onChange }) {
  const set = (val) => onChange({ tfCorrect: val, options: q.options.map((o) => ({ ...o, isCorrect: (o.text === "True") === val })) });
  return (
    <Box sx={{ display: "flex", gap: "8px" }}>
      {[true, false].map((val) => {
        const sel = q.tfCorrect === val;
        return (
          <Box key={String(val)} component="button" type="button" aria-pressed={sel} onClick={() => set(val)}
            sx={{ flex: 1, height: 40, borderRadius: "8px", font: "inherit", fontSize: "14px", fontWeight: 600, cursor: "pointer", border: `1.5px solid ${sel ? W.success : W.line}`, bgcolor: sel ? W.successBg : "#fff", color: sel ? W.success : W.ink2 }}>
            {val ? "True" : "False"}{sel ? " · correct" : ""}
          </Box>
        );
      })}
    </Box>
  );
}

function MatchingEditor({ q, onChange }) {
  const setPair = (idx, patch) => onChange({ pairs: q.pairs.map((p, i) => (i === idx ? { ...p, ...patch } : p)) });
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {q.pairs.map((p, i) => (
        <Box key={p.id} sx={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 16px minmax(0,1fr) 24px", gap: "8px", alignItems: "center" }}>
          <Box component="input" value={p.prompt || ""} aria-label={`Prompt ${i + 1}`} onChange={(e) => setPair(i, { prompt: e.target.value })} sx={inputSx} />
          <Box sx={{ color: W.ink4, textAlign: "center" }}>→</Box>
          <Box component="input" value={p.answer || ""} aria-label={`Answer ${i + 1}`} onChange={(e) => setPair(i, { answer: e.target.value })} sx={inputSx} />
          <Box component="button" type="button" aria-label={`Remove pair ${i + 1}`} onClick={() => onChange({ pairs: q.pairs.filter((_, k) => k !== i) })}
            sx={{ border: 0, bgcolor: "transparent", color: W.ink4, cursor: "pointer", "&:hover": { color: W.danger } }}>✕</Box>
        </Box>
      ))}
      <Box component="button" type="button" onClick={() => onChange({ pairs: [...q.pairs, { id: nextLocalId(), prompt: "", answer: "" }] })}
        sx={{ alignSelf: "flex-start", font: "inherit", fontSize: "12.5px", fontWeight: 700, color: W.ai, border: 0, bgcolor: "transparent", cursor: "pointer", p: 0 }}>
        + Add pair
      </Box>
    </Box>
  );
}

/** Short answer: any number of accepted answers; matching ignores case and extra spaces. */
function ShortAnswerEditor({ q, onChange }) {
  const answers = q.acceptedAnswers && q.acceptedAnswers.length ? q.acceptedAnswers : [""];
  const setAnswer = (idx, value) => onChange({ acceptedAnswers: answers.map((a, i) => (i === idx ? value : a)) });
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <Typography sx={{ fontSize: "12.5px", fontWeight: 700 }}>Accepted answers</Typography>
      {answers.map((a, i) => (
        <Box key={i} sx={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 24px", gap: "8px", alignItems: "center" }}>
          <Box component="input" value={a} aria-label={`Accepted answer ${i + 1}`} onChange={(e) => setAnswer(i, e.target.value)} sx={inputSx} />
          {answers.length > 1 && (
            <Box component="button" type="button" aria-label={`Remove accepted answer ${i + 1}`}
              onClick={() => onChange({ acceptedAnswers: answers.filter((_, k) => k !== i) })}
              sx={{ border: 0, bgcolor: "transparent", color: W.ink4, cursor: "pointer", "&:hover": { color: W.danger } }}>✕</Box>
          )}
        </Box>
      ))}
      <Box component="button" type="button" onClick={() => onChange({ acceptedAnswers: [...answers, ""] })}
        sx={{ alignSelf: "flex-start", font: "inherit", fontSize: "12.5px", fontWeight: 700, color: W.ai, border: 0, bgcolor: "transparent", cursor: "pointer", p: 0 }}>
        + Add accepted answer
      </Box>
    </Box>
  );
}

function QuestionRow({ q, index, expanded, onToggle, onChange, onDelete }) {
  const summary = answerSummary(q);
  const flagged = !!q.needsReview;
  const answered = hasCorrectAnswer(q);
  return (
    <Box sx={{ borderTop: index ? `1px solid ${W.line}` : "none" }}>
      <Box component="button" type="button" onClick={onToggle} aria-expanded={expanded}
        sx={{ width: "100%", display: "grid", gridTemplateColumns: "40px minmax(0,1fr) auto auto 16px", gap: "10px", alignItems: "center", p: "12px 14px", border: 0, font: "inherit", textAlign: "left", cursor: "pointer", color: W.ink, bgcolor: flagged ? W.dangerBg : "transparent", "&:hover": { bgcolor: flagged ? W.dangerBg : "#faf9fb" } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "3px", fontFamily: "var(--font-mono)", fontSize: "12px", color: flagged ? W.danger : W.ink3 }}>
          {String(index + 1).padStart(2, "0")}{flagged && <WarnIcon />}
        </Box>
        <Typography sx={{ fontSize: "14px", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: expanded ? "normal" : "nowrap" }}>
          {q.questionText || <Box component="span" sx={{ color: W.ink4, fontWeight: 400 }}>Untitled question</Box>}
        </Typography>
        {!answered
          ? <Box component="span" sx={{ fontSize: "11px", fontWeight: 700, px: "7px", py: "2px", borderRadius: "5px", bgcolor: W.dangerBg, color: W.danger, whiteSpace: "nowrap" }}>No answer</Box>
          : !flagged && summary
            ? <Box component="span" sx={{ fontSize: "11.5px", fontWeight: 700, px: "7px", py: "2px", borderRadius: "5px", bgcolor: W.successBg, color: W.success, whiteSpace: "nowrap" }}>✓ {summary}</Box>
            : <span />}
        <Box sx={{ fontFamily: "var(--font-mono)", fontSize: "12px", color: W.ink3, whiteSpace: "nowrap" }}>{Number(q.points) || 0} pts</Box>
        <Chevron open={expanded} />
      </Box>

      {expanded && (
        <Box sx={{ p: "4px 14px 16px 64px", display: "flex", flexDirection: "column", gap: "12px" }}>
          <Box>
            <Typography sx={{ fontSize: "12.5px", fontWeight: 700, mb: "6px" }}>Question</Typography>
            <Box component="input" value={q.questionText || ""} aria-label={`Question ${index + 1}`} onChange={(e) => onChange({ questionText: e.target.value })} sx={inputSx} />
          </Box>

          {q.type === "TRUE_FALSE" ? <TrueFalseEditor q={q} onChange={onChange} />
            : q.type === "MATCHING" ? <MatchingEditor q={q} onChange={onChange} />
              : q.type === "SHORT_ANSWER" ? <ShortAnswerEditor q={q} onChange={onChange} />
                : <ChoiceEditor q={q} onChange={onChange} />}

          {flagged && <FlagBanner note={q.reviewNote} quote={q.sourceQuote} onClear={() => onChange({ needsReview: false })} />}

          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Typography sx={{ fontSize: "12.5px", fontWeight: 700 }}>Points</Typography>
            <Box component="input" value={q.points ?? ""} aria-label={`Points for question ${index + 1}`} inputMode="numeric"
              onChange={(e) => onChange({ points: e.target.value === "" ? "" : Number(e.target.value) })}
              sx={{ ...inputSx, width: 64 }} />
            <Box sx={{ flex: 1 }} />
            <Box component="button" type="button" onClick={onDelete}
              sx={{ font: "inherit", fontSize: "13px", fontWeight: 700, color: W.danger, border: 0, bgcolor: "transparent", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "5px" }}>
              <Box component="svg" width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M19 6l-1 14H6L5 6" /></Box>
              Delete question
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

/** Test drafts: one collapsible row per question, flagged ones opened by default. */
export default function TestQuestionReview({ draft, onChange }) {
  const [open, setOpen] = useState(() => new Set(draft.list.filter((q) => q.needsReview).map((q) => q.id)));
  const allOpen = draft.list.length > 0 && draft.list.every((q) => open.has(q.id));
  const toggle = (id) => setOpen((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const updQ = (idx, patch) => onChange({ list: draft.list.map((q, i) => (i === idx ? { ...q, ...patch } : q)) });

  const types = [...new Set(draft.list.map((q) => q.type))];
  const typeLabel = types.length === 1 ? TYPE_LABEL[types[0]] || types[0] : types.length ? "mixed types" : null;

  const addQuestion = () => {
    const q = normalizeQuestion({ type: "MULTIPLE_CHOICE", questionText: "", points: 1, options: [{ text: "" }, { text: "" }, { text: "" }, { text: "" }] });
    onChange({ list: [...draft.list, q] });
    setOpen((prev) => new Set(prev).add(q.id));
  };

  return (
    <Box sx={{ border: `1px solid ${W.line}`, borderRadius: "10px", overflow: "hidden" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: "10px 14px", borderBottom: `1px solid ${W.line}` }}>
        <Typography sx={{ fontSize: "12.5px", color: W.ink3 }}>
          {draft.list.length} question{draft.list.length === 1 ? "" : "s"}{typeLabel ? ` · ${typeLabel}` : ""}
        </Typography>
        {draft.list.length > 0 && (
          <Box component="button" type="button" onClick={() => setOpen(allOpen ? new Set() : new Set(draft.list.map((q) => q.id)))}
            sx={{ font: "inherit", fontSize: "13px", fontWeight: 700, color: W.ai, border: 0, bgcolor: "transparent", cursor: "pointer" }}>
            {allOpen ? "Collapse all" : "Expand all"}
          </Box>
        )}
      </Box>

      {draft.list.map((q, i) => (
        <QuestionRow key={q.id} q={q} index={i} expanded={open.has(q.id)} onToggle={() => toggle(q.id)}
          onChange={(patch) => updQ(i, patch)} onDelete={() => onChange({ list: draft.list.filter((_, k) => k !== i) })} />
      ))}

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: "10px 14px", borderTop: `1px solid ${W.line}` }}>
        <Box component="button" type="button" onClick={addQuestion}
          sx={{ font: "inherit", fontSize: "13px", fontWeight: 700, color: W.ai, border: 0, bgcolor: "transparent", cursor: "pointer" }}>
          + Add question
        </Box>
        <Typography sx={{ fontSize: "13px" }}>Total <b>{totalPoints(draft.list, "points")}</b> pts</Typography>
      </Box>
    </Box>
  );
}
